import "vue/jsx";
import { createApp } from "vue";
import "./../index.css";
import { App } from "./App";
import { router } from "./router";
import * as Sentry from "@sentry/vue";
import { QueryClient, VueQueryPlugin } from "@tanstack/vue-query";
import { release } from "../version";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});
//
const app = createApp(App);
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    ignoreErrors: [
      /mixpanel/i,
      /amplitude/i,
      /Java object is gone/i,
      /window.webkit.messageHandlers/gi,
      /NavigationTypeDetection/i,
      /initCookieConsent/gi,
      "Failed to fetch",
      "Load failed",
      "TypeError: Failed to fetch",
      "TypeError: NetworkError when attempting to fetch resource.",
    ],
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // Source of truth for the Sentry release is `release` in src/version.ts.
    // `sentry:sourcemaps` uploads under the same value (read via
    // scripts/print-release.mjs) so browser stack traces resolve.
    release,
    integrations: [
      Sentry.httpContextIntegration(),
      Sentry.httpClientIntegration(),
      //      Sentry.browserProfilingIntegration(),
      Sentry.browserSessionIntegration(),
      Sentry.dedupeIntegration(),
      Sentry.extraErrorDataIntegration(),
      Sentry.browserTracingIntegration({
        router,
        shouldCreateSpanForRequest: (url) => {
          return !(
            /\/cdn-cgi\/rum/i.test(url) ||
            /posthog\.com/i.test(url) ||
            /amplitude/i.test(url) ||
            /mixpanel/i.test(url) ||
            /\/assets\/.*\.json$/i.test(url)
          );
        },
        enableLongAnimationFrame: false,
        enableLongTask: false,
        ignoreResourceSpans: [
          "resource.css",
          "resource.script",
          "resource.link",
          "resource.img",
          "resource.other",
        ],
      }),
      Sentry.linkedErrorsIntegration(),
    ].filter((el) => el) as any,
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Session Replay
    replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

app.use(VueQueryPlugin, { queryClient });
app.use(router);
app.mount("#root");

// Test helper — only active in non-production for Playwright E2E tests.
if (import.meta.env.VITE_ENV !== "production") {
  (window as any).testAPI = {
    loginCustomer: async (email: string) => {
      const res = await fetch("/api/v3/customer/session/test-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
        credentials: "include",
      });
      if (!res.ok) throw new Error(`test-login failed: ${res.status}`);
      return res.json();
    },
  };
}
